export interface TemplateTheme {
  id: string;
  name: string;
  category: string;
  colors: string[];
  cssVars: Record<string, string>;
  sections: {
    type: string;
    h: number;
    bg: string;
    accent: string;
  }[];
  /** If set, render as iframe pointing to this static HTML file */
  htmlPath?: string;
  /** Premium series metadata (optional, used in catalog UI) */
  description?: string;
  theme?: string;
  useCases?: string[];
  primaryColor?: string;
}

export const templateThemes: TemplateTheme[] = [
  // ─── Premium Base44-grade series (Sprint 1) ─────────────────────────────
  {
    id: "immobilier-luxe-premium",
    name: "Immobilier Prestige",
    description: "Cinématique, or sur obsidienne. Pour l'immobilier de très haut de gamme.",
    category: "Immobilier",
    theme: "Dark · Cinematic · Gold",
    useCases: ["Hôtels particuliers", "Biens d'exception", "Agences haut de gamme"],
    primaryColor: "#D4AF37",
    colors: ["#0A0A0A", "#D4AF37", "#F5F0E6"],
    cssVars: {
      "--template-bg": "#0A0A0A",
      "--template-primary": "45 65% 52%",
      "--template-accent": "#D4AF37",
      "--template-surface": "#141414",
      "--template-text": "#F5F0E6",
    },
    sections: [
      { type: "hero", h: 50, bg: "#0A0A0A", accent: "#D4AF37" },
      { type: "villa", h: 25, bg: "#141414", accent: "#D4AF37" },
      { type: "services", h: 15, bg: "#0A0A0A", accent: "#D4AF37" },
      { type: "footer", h: 10, bg: "#0A0A0A", accent: "#D4AF37" },
    ],
  },
  {
    id: "librairie",
    name: "Librairie Littéraire",
    description: "Éditorial, papier et encre. Esprit maison d'édition indépendante.",
    category: "Commerce",
    theme: "Paper · Serif · Timeless",
    useCases: ["Librairies indépendantes", "Maisons d'édition", "Bookshops"],
    primaryColor: "#8C271E",
    colors: ["#F5EFE6", "#8C271E", "#1A1A1A"],
    cssVars: {
      "--template-bg": "#F5EFE6",
      "--template-primary": "4 65% 33%",
      "--template-accent": "#8C271E",
      "--template-surface": "#EDE4D3",
      "--template-text": "#1A1A1A",
    },
    sections: [
      { type: "hero", h: 40, bg: "#F5EFE6", accent: "#8C271E" },
      { type: "shelves", h: 30, bg: "#EDE4D3", accent: "#8C271E" },
      { type: "quote", h: 20, bg: "#8C271E", accent: "#F5EFE6" },
      { type: "footer", h: 10, bg: "#1A1A1A", accent: "#F5EFE6" },
    ],
  },
  {
    id: "saas-yc",
    name: "SaaS · Y Combinator",
    description: "Bold minimal avec code snippet. Style Stripe / Vercel / Linear.",
    category: "Tech",
    theme: "Clean · Bold · Dev-first",
    useCases: ["Startups SaaS", "Dev tools", "API products"],
    primaryColor: "#635BFF",
    colors: ["#FFFFFF", "#635BFF", "#0A0A0A"],
    cssVars: {
      "--template-bg": "#FFFFFF",
      "--template-primary": "244 100% 68%",
      "--template-accent": "#635BFF",
      "--template-surface": "#F6F6F8",
      "--template-text": "#0A0A0A",
    },
    sections: [
      { type: "hero", h: 40, bg: "#FFFFFF", accent: "#635BFF" },
      { type: "code", h: 25, bg: "#0A0A0A", accent: "#635BFF" },
      { type: "logos", h: 15, bg: "#F6F6F8", accent: "#0A0A0A" },
      { type: "footer", h: 20, bg: "#FFFFFF", accent: "#635BFF" },
    ],
  },
  {
    id: "avocat",
    name: "Cabinet Avocat / Conseil",
    description: "Navy + or. Autorité, discrétion, prestige de l'institution.",
    category: "Services",
    theme: "Navy · Serif · Institutional",
    useCases: ["Cabinets d'avocats", "Cabinets de conseil", "Notaires"],
    primaryColor: "#0B192C",
    colors: ["#0B192C", "#C9A961", "#F4EFE6"],
    cssVars: {
      "--template-bg": "#0B192C",
      "--template-primary": "42 46% 59%",
      "--template-accent": "#C9A961",
      "--template-surface": "#112240",
      "--template-text": "#F4EFE6",
    },
    sections: [
      { type: "hero", h: 45, bg: "#0B192C", accent: "#C9A961" },
      { type: "expertise", h: 25, bg: "#F4EFE6", accent: "#0B192C" },
      { type: "team", h: 20, bg: "#112240", accent: "#C9A961" },
      { type: "footer", h: 10, bg: "#0B192C", accent: "#C9A961" },
    ],
  },
  {
    id: "portfolio-premium",
    name: "Studio Créatif Éditorial",
    description: "Awwwards-grade. Typographie massive, bento asymétrique, oranges sanguine.",
    category: "Créatif",
    theme: "Editorial · Bold · Artistic",
    useCases: ["Studios de design", "Agences créatives", "Portfolios pro"],
    primaryColor: "#FF5722",
    colors: ["#FAF7F2", "#FF5722", "#0A0A0A"],
    cssVars: {
      "--template-bg": "#FAF7F2",
      "--template-primary": "14 100% 57%",
      "--template-accent": "#FF5722",
      "--template-surface": "#F0ECE4",
      "--template-text": "#0A0A0A",
    },
    sections: [
      { type: "hero", h: 40, bg: "#FAF7F2", accent: "#FF5722" },
      { type: "bento", h: 35, bg: "#F0ECE4", accent: "#FF5722" },
      { type: "manifesto", h: 15, bg: "#FF5722", accent: "#FAF7F2" },
      { type: "footer", h: 10, bg: "#0A0A0A", accent: "#FF5722" },
    ],
  },

  // ─── Legacy catalog ────────────────────────────────────────────────────
  // ─── IMMOBILIER ────────────────────────────────────────
  {
    id: "immo-luxe",
    name: "Immobilier Luxe",
    category: "Immobilier",
    colors: ["#0D0D0D", "#C9A962", "#FAFAFA"],
    cssVars: {
      "--template-bg": "#0D0D0D",
      "--template-primary": "43 52% 59%",
      "--template-accent": "#C9A962",
      "--template-surface": "#1A1A1A",
      "--template-text": "#FAFAFA",
    },
    sections: [
      { type: "hero", h: 40, bg: "#0D0D0D", accent: "#C9A962" },
      { type: "features", h: 20, bg: "#1A1A1A", accent: "#C9A962" },
      { type: "gallery", h: 25, bg: "#0D0D0D", accent: "#C9A962" },
      { type: "footer", h: 10, bg: "#0D0D0D", accent: "#C9A962" },
    ],
    htmlPath: "/templates/immobilier/luxe.html",
  },
  {
    id: "immo-moderne",
    name: "Immobilier Moderne",
    category: "Immobilier",
    colors: ["#FFFFFF", "#2563EB", "#111827"],
    cssVars: {
      "--template-bg": "#FFFFFF",
      "--template-primary": "217 91% 60%",
      "--template-accent": "#2563EB",
      "--template-surface": "#F3F4F6",
      "--template-text": "#111827",
    },
    sections: [
      { type: "hero", h: 35, bg: "#FFFFFF", accent: "#2563EB" },
      { type: "features", h: 25, bg: "#F3F4F6", accent: "#2563EB" },
      { type: "cta", h: 15, bg: "#2563EB", accent: "#FFFFFF" },
      { type: "footer", h: 10, bg: "#111827", accent: "#2563EB" },
    ],
    htmlPath: "/templates/immobilier/moderne.html",
  },
  {
    id: "immo-crm",
    name: "Immobilier CRM",
    category: "Immobilier",
    colors: ["#0F172A", "#6366F1", "#F1F5F9"],
    cssVars: {
      "--template-bg": "#0F172A",
      "--template-primary": "239 84% 67%",
      "--template-accent": "#6366F1",
      "--template-surface": "#1E293B",
      "--template-text": "#F1F5F9",
    },
    sections: [
      { type: "hero", h: 25, bg: "#0F172A", accent: "#6366F1" },
      { type: "dashboard", h: 35, bg: "#F1F5F9", accent: "#6366F1" },
      { type: "features", h: 20, bg: "#0F172A", accent: "#6366F1" },
      { type: "footer", h: 8, bg: "#0F172A", accent: "#6366F1" },
    ],
    htmlPath: "/templates/immobilier/crm.html",
  },
  // ─── AUTRES CATÉGORIES ─────────────────────────────────
  {
    id: "premium",
    name: "Corporate Pro",
    category: "Business",
    colors: ["#1a1a2e", "#e2b857", "#f5f5f0"],
    cssVars: {
      "--template-bg": "#1a1a2e",
      "--template-primary": "45 70% 49%",
      "--template-accent": "#e2b857",
      "--template-surface": "#f5f5f0",
      "--template-text": "#f5f5f0",
    },
    sections: [
      { type: "hero", h: 40, bg: "#1a1a2e", accent: "#e2b857" },
      { type: "features", h: 20, bg: "#f5f5f0", accent: "#1a1a2e" },
      { type: "cta", h: 15, bg: "#e2b857", accent: "#1a1a2e" },
      { type: "footer", h: 10, bg: "#1a1a2e", accent: "#e2b857" },
    ],
  },
  {
    id: "ecommerce",
    name: "Boutique",
    category: "E-Commerce",
    colors: ["#ffffff", "#10b981", "#1f2937"],
    cssVars: {
      "--template-bg": "#ffffff",
      "--template-primary": "160 84% 39%",
      "--template-accent": "#10b981",
      "--template-surface": "#f9fafb",
      "--template-text": "#1f2937",
    },
    sections: [
      { type: "hero", h: 30, bg: "#ffffff", accent: "#10b981" },
      { type: "grid", h: 35, bg: "#f9fafb", accent: "#10b981" },
      { type: "banner", h: 12, bg: "#10b981", accent: "#ffffff" },
      { type: "footer", h: 8, bg: "#1f2937", accent: "#10b981" },
    ],
  },
  {
    id: "portfolio",
    name: "Créatif",
    category: "Portfolio",
    colors: ["#0f0f0f", "#8b5cf6", "#fafafa"],
    cssVars: {
      "--template-bg": "#0f0f0f",
      "--template-primary": "258 90% 66%",
      "--template-accent": "#8b5cf6",
      "--template-surface": "#1a1a1a",
      "--template-text": "#fafafa",
    },
    sections: [
      { type: "hero", h: 35, bg: "#0f0f0f", accent: "#8b5cf6" },
      { type: "gallery", h: 30, bg: "#0f0f0f", accent: "#8b5cf6" },
      { type: "about", h: 15, bg: "#fafafa", accent: "#0f0f0f" },
      { type: "footer", h: 8, bg: "#0f0f0f", accent: "#8b5cf6" },
    ],
  },
  {
    id: "restaurant",
    name: "Gastro",
    category: "Restaurant",
    colors: ["#1c1917", "#f97316", "#fef3c7"],
    cssVars: {
      "--template-bg": "#1c1917",
      "--template-primary": "25 95% 53%",
      "--template-accent": "#f97316",
      "--template-surface": "#fef3c7",
      "--template-text": "#fef3c7",
    },
    sections: [
      { type: "hero", h: 38, bg: "#1c1917", accent: "#f97316" },
      { type: "menu", h: 25, bg: "#fef3c7", accent: "#1c1917" },
      { type: "cta", h: 12, bg: "#f97316", accent: "#ffffff" },
      { type: "footer", h: 10, bg: "#1c1917", accent: "#f97316" },
    ],
  },
  {
    id: "saas",
    name: "TechStart",
    category: "SaaS",
    colors: ["#0f172a", "#3b82f6", "#f8fafc"],
    cssVars: {
      "--template-bg": "#0f172a",
      "--template-primary": "217 91% 60%",
      "--template-accent": "#3b82f6",
      "--template-surface": "#f8fafc",
      "--template-text": "#f8fafc",
    },
    sections: [
      { type: "hero", h: 35, bg: "#0f172a", accent: "#3b82f6" },
      { type: "features", h: 25, bg: "#f8fafc", accent: "#3b82f6" },
      { type: "pricing", h: 18, bg: "#0f172a", accent: "#3b82f6" },
      { type: "footer", h: 8, bg: "#0f172a", accent: "#3b82f6" },
    ],
  },
  {
    id: "artisan",
    name: "Artisan",
    category: "Services",
    colors: ["#ffffff", "#d97706", "#292524"],
    cssVars: {
      "--template-bg": "#ffffff",
      "--template-primary": "38 92% 44%",
      "--template-accent": "#d97706",
      "--template-surface": "#fafaf9",
      "--template-text": "#292524",
    },
    sections: [
      { type: "hero", h: 35, bg: "#ffffff", accent: "#d97706" },
      { type: "services", h: 25, bg: "#fafaf9", accent: "#292524" },
      { type: "testimonial", h: 15, bg: "#d97706", accent: "#ffffff" },
      { type: "footer", h: 10, bg: "#292524", accent: "#d97706" },
    ],
  },
  {
    id: "wix-consultant-rdv",
    name: "Consultant avec rendez-vous",
    description: "Crème, bleu appuyé et formes géométriques, inspiré du modèle Wix.",
    category: "Services",
    primaryColor: "#2A3BF0",
    colors: ["#F4F1EC", "#2A3BF0", "#D6F76B"],
    cssVars: { "--template-bg": "#F4F1EC", "--template-primary": "233 88% 56%", "--template-accent": "#D6F76B", "--template-surface": "#B0B9F0", "--template-text": "#2A3BF0" },
    sections: [{ type: "hero", h: 50, bg: "#F4F1EC", accent: "#2A3BF0" }, { type: "services", h: 30, bg: "#2A3BF0", accent: "#D6F76B" }],
  },
];

export const getThemeById = (id: string): TemplateTheme =>
  templateThemes.find((t) => t.id === id) || templateThemes[0];

export const getCategories = (): string[] =>
  [...new Set(templateThemes.map((t) => t.category))];

export const getTemplatesByCategory = (category: string): TemplateTheme[] =>
  templateThemes.filter((t) => t.category === category);
